/**
 * Coupon Management
 * Handles coupon drawer, form submission, display, and Store API integration
 */

import { store } from '@wordpress/interactivity';
import { formatPrice } from '../shared/formatters.js';
import { slideUp, slideDown } from '../shared/ui-utils.js';

/**
 * Initialize coupon drawer toggle functionality
 */
export function initializeCouponDrawer() {

	// Handle coupon drawer toggle
	document.addEventListener('click', function(e) {
		// Check if clicked element is the coupon title or within it
		const couponTitle = e.target.closest('.cc-coupon-title');

		if (couponTitle) {
			e.preventDefault();

			const couponForm = document.querySelector('.cc-coupon-form');
			const couponWrapper = document.querySelector('.cc-coupon');

			if (couponForm && couponWrapper) {
				// Toggle using jQuery-like slideDown/slideUp approach
				const isHidden = window.getComputedStyle(couponForm).display === 'none';

				if (isHidden) {
					// Open the drawer
					couponWrapper.classList.add('cc-coupon-open');
					slideDown(couponForm, 300);
				} else {
					// Close the drawer
					slideUp(couponForm, 300, function() {
						couponWrapper.classList.remove('cc-coupon-open');
					});
				}
			}
		}
	});

	// Handle error notice close button (if there's an X or close area)
	document.addEventListener('click', function(e) {
		if (e.target.matches('.cc-coupon .woocommerce-error')) {
			const error = e.target;
			const clickX = e.pageX - error.getBoundingClientRect().left;

			// If clicked near the right edge (last 40px), close the error
			if (clickX > error.offsetWidth - 40) {
				const wrapper = error.closest('.woocommerce-notices-wrapper');
				if (wrapper) {
					wrapper.style.transition = 'opacity 200ms ease';
					wrapper.style.opacity = '0';
					setTimeout(() => {
						wrapper.style.display = 'none';
					}, 200);
				}
			}
		}
	});

}

/**
 * Initialize coupon form functionality with Store API
 * @param {Function} updateCartTotals - Function to update cart totals
 */
export function initializeCouponForm(updateCartTotals) {

	// Handle coupon form submission
	document.addEventListener('submit', async function(e) {
		if (e.target && e.target.id === 'apply_coupon_form') {
			e.preventDefault();

			const couponInput = document.getElementById('cc_coupon_code');
			const couponCode = couponInput ? couponInput.value.trim() : '';

			if (!couponCode) {
				return;
			}

			await applyCoupon(couponCode, updateCartTotals);
		}
	});

	// Handle coupon removal
	document.addEventListener('click', async function(e) {
		const removeButton = e.target.closest('.cc-remove-coupon');
		if (removeButton) {
			e.preventDefault();

			const couponElement = removeButton.closest('.cc-applied-coupon');
			const couponCode = couponElement ? couponElement.querySelector('.cc_applied_code').textContent.trim() : '';

			if (couponCode) {
				await removeCoupon(couponCode, updateCartTotals);
			}
		}
	});

}

/**
 * Update the applied coupons display in the DOM
 * @param {Array} coupons - Array of applied coupons from Store API
 */
export function updateAppliedCouponsDisplay(coupons) {

	let discountsContainer = document.querySelector('.cc-discounts');

	if (!discountsContainer) {
		// If container doesn't exist and we have coupons, create it
		if (coupons.length > 0) {
			createDiscountsContainer(coupons);
			// Update the savings display after creating the container
			updateCouponSavingsDisplay(coupons);
		}
		return;
	}

	if (coupons.length === 0) {
		// No coupons applied, hide the entire discounts container
		discountsContainer.style.display = 'none';
		return;
	}

	// Show the discounts container and update the coupons list
	discountsContainer.style.display = '';
	const discountDiv = discountsContainer.querySelector('.cc-discount');
	if (discountDiv) {
		// Clear existing coupons
		discountDiv.innerHTML = '';

		// Add each applied coupon
		coupons.forEach(coupon => {
			const couponElement = createCouponElement(coupon.code);
			discountDiv.appendChild(couponElement);
		});
	}

	// Update the savings amount if available
	updateCouponSavingsDisplay(coupons);
}

/**
 * Create the discounts container if it doesn't exist
 * @param {Array} coupons - Array of applied coupons
 */
function createDiscountsContainer(coupons) {
	// Find the totals section (cc-totals) to insert the discounts before it
	const totalsSection = document.querySelector('.cc-totals');

	if (totalsSection) {
		// Insert before totals section for correct placement (after coupon, before subtotal/checkout)
		const discountsHTML = `
			<div class="cc-discounts">
				<div class="cc-discount">
					${coupons.map(coupon => createCouponHTML(coupon.code)).join('')}
				</div>
				<div class="cc-savings"></div>
			</div>
		`;
		totalsSection.insertAdjacentHTML('beforebegin', discountsHTML);
	} else {
		// Fallback to after coupon section if totals not found
		const couponSection = document.querySelector('.cc-coupon');
		if (!couponSection) return;

		const discountsHTML = `
			<div class="cc-discounts">
				<div class="cc-discount">
					${coupons.map(coupon => createCouponHTML(coupon.code)).join('')}
				</div>
				<div class="cc-savings"></div>
			</div>
		`;
		couponSection.insertAdjacentHTML('afterend', discountsHTML);
	}
}

/**
 * Create HTML for a single coupon element
 * @param {string} code - Coupon code
 * @returns {string} HTML string
 */
function createCouponHTML(code) {
	const pluginDir = window.caddyConfig?.pluginDir || '/wp-content/plugins/caddy/';
	// Escape code to prevent XSS via innerHTML
	const safeCode = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	return `
		<div class="cc-applied-coupon">
			<img src="${pluginDir}public/img/tag-icon.svg" alt="Discount Code">
			<span class="cc_applied_code">${safeCode}</span>
			<a href="javascript:void(0);" class="cc-remove-coupon"><i class="ccicon-close"></i></a>
		</div>
	`;
}

/**
 * Create a DOM element for a single coupon
 * @param {string} code - Coupon code
 * @returns {HTMLElement} Coupon element
 */
function createCouponElement(code) {
	const couponDiv = document.createElement('div');
	couponDiv.className = 'cc-applied-coupon';
	couponDiv.innerHTML = createCouponHTML(code);
	return couponDiv.firstElementChild;
}

/**
 * Update the savings display for applied coupons
 * @param {Array} coupons - Array of applied coupons
 */
function updateCouponSavingsDisplay(coupons) {
	const savingsContainer = document.querySelector('.cc-savings');
	if (!savingsContainer) {
		return;
	}

	// Get the current state to access totals
	const { state } = store('caddy/cart');

	// Use stored discount total if available, otherwise try to calculate
	let discount = state.discountTotal || 0;


	// If no stored discount, calculate by comparing original subtotal with current total
	if (discount === 0 && state.items) {
		const originalSubtotal = state.items.reduce((total, item) => total + (item.lineTotal || 0), 0);
		const currentTotal = state.cartSubtotal || 0;
		discount = originalSubtotal - currentTotal;
	}

	// Display the savings if there's a discount
	if (discount > 0) {
		savingsContainer.innerHTML = `<span class="cc-discount-amount">-${formatPrice(discount)}</span>`;
	} else {
		savingsContainer.innerHTML = '';
	}
}

/**
 * Apply a coupon using the Store API
 * @param {string} couponCode - The coupon code to apply
 * @param {Function} updateCartTotals - Function to update cart totals
 */
async function applyCoupon(couponCode, updateCartTotals) {

	const cartContainer = document.querySelector('#cc-cart');

	// Show loading state
	if (cartContainer) {
		cartContainer.style.opacity = '0.3';
	}

	// Clear any existing notices
	const noticesWrapper = document.querySelector('.cc-coupon .woocommerce-notices-wrapper');
	if (noticesWrapper) {
		noticesWrapper.innerHTML = '';
	}

	try {
		// Get the Store API nonce
		const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');

		const response = await fetch('/wp-json/wc/store/v1/cart/apply-coupon', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(storeApiNonce ? { 'Nonce': storeApiNonce } : {})
			},
			credentials: 'same-origin',
			body: JSON.stringify({
				code: couponCode
			})
		});

		const data = await response.json();

		if (!response.ok) {
			const { state } = store('caddy/cart');
			throw new Error(data.message || state.i18n.errorApplyCouponFailed);
		}


		// Update only the totals and pricing - don't touch cart items
		const { state } = store('caddy/cart');

		// Update totals using centralized helper function
		updateCartTotals(state, data);
		state.coupons = data.coupons || [];
		// Store discount amount if available (or clear it when removing coupons)
		state.discountTotal = data.totals.total_discount ? parseFloat(data.totals.total_discount) / 100 : 0;

		// Update applied coupons display
		updateAppliedCouponsDisplay(data.coupons || []);

		// Clear the input field
		const couponInput = document.getElementById('cc_coupon_code');
		if (couponInput) {
			couponInput.value = '';
		}

		// Close the coupon drawer
		const couponForm = document.querySelector('.cc-coupon-form');
		const couponWrapper = document.querySelector('.cc-coupon');
		if (couponForm && couponWrapper) {
			slideUp(couponForm, 300, function() {
				couponWrapper.classList.remove('cc-coupon-open');
			});
		}

		// Show success message (optional)

	} catch (error) {

		// Show error message
		if (noticesWrapper) {
			const { state } = store('caddy/cart');
			while (noticesWrapper.firstChild) noticesWrapper.removeChild(noticesWrapper.firstChild);
			const errorDiv = document.createElement('div');
			errorDiv.className = 'woocommerce-error';
			errorDiv.setAttribute('role', 'alert');
			errorDiv.textContent = error.message || state.i18n.errorApplyCouponTryAgain;
			noticesWrapper.appendChild(errorDiv);
		}
	} finally {
		// Remove loading state
		if (cartContainer) {
			cartContainer.style.opacity = '1';
		}
	}
}

/**
 * Remove a coupon using the Store API
 * @param {string} couponCode - The coupon code to remove
 * @param {Function} updateCartTotals - Function to update cart totals
 */
async function removeCoupon(couponCode, updateCartTotals) {

	const cartContainer = document.querySelector('#cc-cart');

	// Show loading state
	if (cartContainer) {
		cartContainer.style.opacity = '0.3';
	}

	try {
		// Get the Store API nonce
		const storeApiNonce = document.querySelector('meta[name="wc-store-api-nonce"]')?.getAttribute('content');

		const response = await fetch('/wp-json/wc/store/v1/cart/remove-coupon', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(storeApiNonce ? { 'Nonce': storeApiNonce } : {})
			},
			credentials: 'same-origin',
			body: JSON.stringify({
				code: couponCode
			})
		});

		const data = await response.json();

		if (!response.ok) {
			const { state } = store('caddy/cart');
			throw new Error(data.message || state.i18n.errorRemoveCouponFailed);
		}


		// Update only the totals and pricing - don't touch cart items
		const { state } = store('caddy/cart');

		// Update totals using centralized helper function
		updateCartTotals(state, data);
		state.coupons = data.coupons || [];
		// Store discount amount if available (or clear it when removing coupons)
		state.discountTotal = data.totals.total_discount ? parseFloat(data.totals.total_discount) / 100 : 0;

		// Update applied coupons display
		updateAppliedCouponsDisplay(data.coupons || []);


	} catch (error) {

		// Show error message
		const noticesWrapper = document.querySelector('.cc-coupon .woocommerce-notices-wrapper');
		if (noticesWrapper) {
			const { state } = store('caddy/cart');
			while (noticesWrapper.firstChild) noticesWrapper.removeChild(noticesWrapper.firstChild);
			const errorDiv = document.createElement('div');
			errorDiv.className = 'woocommerce-error';
			errorDiv.setAttribute('role', 'alert');
			errorDiv.textContent = error.message || state.i18n.errorRemoveCouponTryAgain;
			noticesWrapper.appendChild(errorDiv);
		}
	} finally {
		// Remove loading state
		if (cartContainer) {
			cartContainer.style.opacity = '1';
		}
	}
}
